import React, { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { TDispatch, TRootState } from "../../../redux/types";
import { useHistory, useLocation } from "react-router-dom";
import { Flex } from "reflexbox";

import { Column } from "@components/ExpandableRow/styles";
import Drawer from "@components/Drawer";
import { OldInput } from "@components/OldInput";
import ExpandableRow from "@components/ExpandableRow";
import { Button } from "@components/Button";
import { useSwal } from "@hooks/useSwal";
import { useFormatMessage } from "@hooks/formatMessage";
import { AddButton } from "@components/AddButton";
import { ScrollableList } from "@components/ScrollableList";
import { Text } from "@components/Text";
import { Checkbox } from "@components/Checkbox";

import { deserialize } from "../../../api/jsonApiParser";
import ApplicationsModal from "./Modal/ApplicationsModal";
import { getParameterByName } from "../../../utils/toSearchParams";

import { ReactComponent as IconEdit } from "../../../assets/icons/edit.svg";
import { ReactComponent as IconTrash } from "../../../assets/icons/trash.svg";
import { ReactComponent as IconFilter } from "../../../assets/icons/filter.svg";
import {
  setApplicationsPageNo,
  incrementApplicationsPageNo,
  fetchApplicationsList,
  deleteApplication,
  getApplicationById,
} from "./ducks";

import "../../../styles/index.scss";
import "./Applications.scss";

enum formMode {
  edit = "edit",
  password = "password",
}

const Applications: FC = () => {
  const [filtersOpened, setFiltersOpened] = useState(window.innerWidth >= 1400);
  const [modalContent, setModalContent] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(undefined);
  const [addApplicationForm, setaddApplicationForm] = useState(undefined);
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();
  const [showMoreSpinner, setShowMoreSpinner] = useState(false);
  const [applicationsFilter, setApplicationsFilter] = useState({
    GenericText: null,
  });

  const history = useHistory();
  const location = useLocation();
  const formatMessage = useFormatMessage();
  const dispatch: TDispatch = useDispatch();

  const { applicationsList, moreApplications, applicationsPageNo } = useSelector(
    ({
      ApplicationsReducer: { applications, moreApplications, applicationsPageNo },
    }: TRootState) => ({
      applicationsList: applications,
      moreApplications,
      applicationsPageNo,
    }),
    shallowEqual
  );

  if (location.search && location.search.length > 0) {
    setModalContent(getParameterByName("content", location.search));
    setModalIsOpen(!!modalContent);
  }

  useEffect(() => {
    dispatch(fetchApplicationsList()).then(() => dispatch(incrementApplicationsPageNo()));
  }, [dispatch]);

  const handleLoadMore = () => {
    if (!showMoreSpinner && moreApplications) {
      setShowMoreSpinner(true);
      dispatch(fetchApplicationsList(applicationsPageNo, 10, applicationsFilter)).then(() => {
        setShowMoreSpinner(false);
        dispatch(incrementApplicationsPageNo());
      });
    }
  };

  const changeFilterOpened = () => {
    setFiltersOpened(prevState => !prevState);
  };

  const filterApplications = () => {
    dispatch(setApplicationsPageNo(1));
    dispatch(fetchApplicationsList(1, 10, applicationsFilter));
  };

  const clearFilters = () => {
    setApplicationsFilter({
      GenericText: null,
    });

    dispatch(setApplicationsPageNo(1));
    dispatch(fetchApplicationsList(1, 10));
  };

  const openModal = (content, message?, entry?) => {
    if (content === formMode.edit) {
      getApplicationById(entry.id).then(ret => {
        if (ret) {
          setaddApplicationForm(deserialize(ret.data));
          setModalIsOpen(true);
          setModalContent(content);
        }
      });
    } else {
      setaddApplicationForm({ id: entry.id });
      setModalIsOpen(true);
      setModalContent(content);
    }
  };

  const saveHistoryAndFilterApplications = () => {
    history.push({
      search: "",
    });
    filterApplications();
  };

  const closeModal = () => {
    setModalIsOpen(false);
    saveHistoryAndFilterApplications();

    // TODO: this.props.fetchCompanies().then(res => this.setState({ parentCompanyList: res }));
    // TODO: change the location of the list to the modal component
  };

  const showSuccessMessage = (action, callbackFunction) => {
    swalAlertWithCallback(
      {
        text: `Application has been ${action}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      callbackFunction
    );
  };

  const remove = id => {
    swalConfirmationMessage({
      text: "Do you really want to remove this application?",
      type: "info",
      cancelationText: "Cancel",
      confirmationText: "Remove application",
    }).then(value => {
      if (value) {
        deleteApplication(id).then(ret => {
          if (ret) showSuccessMessage("removed", saveHistoryAndFilterApplications);
        });
      }
    });
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Large Modal"
        className={"large-modal"}
        overlayClassName={"large-modal-overlay"}
      >
        {modalIsOpen && (
          <ApplicationsModal closeModal={closeModal} initialData={addApplicationForm} />
        )}
      </Modal>

      <Flex pl={["10px", "80px"]} pr={["10px", 0]} height="100%" width="100%">
        <Flex flexDirection="column" width="100%">
          <AddButton
            id="addApplicationBtn"
            p="0 32px"
            m="32px 0"
            label={formatMessage("app.addnewapplication", "Add Application")}
            onClick={() => {
              setaddApplicationForm({});
              setModalIsOpen(true);
            }}
          />
          <ScrollableList
            items={applicationsList}
            onLoadMore={handleLoadMore}
            renderHeader={
              <>
                <Column>
                  <Text>{formatMessage("app.application", "Application")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.url", "Url")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.pathLocal", "Path")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.debuggMode", "Debug")}</Text>
                </Column>
                <Column icon>
                  <Text>
                    <div
                      id="toggleFilterArea"
                      className="icon-filter"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation();
                        changeFilterOpened();
                      }}
                    >
                      <IconFilter />
                    </div>
                  </Text>
                </Column>
              </>
            }
            renderItem={(item, index) => (
              <ExpandableRow
                key={"applicationRow" + index}
                id={item.id as string}
                translucent={false}
                expandable={false}
                top={
                  <React.Fragment>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.applicationName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.url}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.pathLocal}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Checkbox
                          id="TableDebuggModeInput"
                          value={(item.debuggMode as string) || ""}
                          checked={item.debuggMode as boolean}
                          wrapperProps={{ width: "20%" }}
                        />
                      </div>
                    </Column>
                    <Column icon>
                      <div className="d-flex flex-row showChildOnHover">
                        <div
                          data-cy="changeEntry"
                          className="iconWrapper pointer large"
                          title="Edit Application"
                          style={{}}
                          onClick={() => openModal(formMode.edit, "", item)}
                        >
                          <IconEdit />
                        </div>
                        <div
                          data-cy="deleteEntry"
                          className="iconWrapper pointer large"
                          title="Delete Application"
                          style={{}}
                          onClick={() => remove(item.id)}
                        >
                          <IconTrash />
                        </div>
                      </div>
                    </Column>
                  </React.Fragment>
                }
              />
            )}
          />
        </Flex>
        <Drawer id="filterArea" opened={filtersOpened}>
          <div className="paddingContainer">
            <p className="colorGrey mb-1">Dynamic Filter</p>
            <div data-cy="dynamicFilterByField">
              <OldInput
                id="dynamicSearch"
                name="dynamicSearch"
                key="dynamicSearch"
                inputWrapperClass="text-input filter-input"
                placeholder={formatMessage("app.anyfield", "Any Field")}
                value={applicationsFilter.GenericText}
                onChange={(name, value) =>
                  setApplicationsFilter(prevState => ({ ...prevState, GenericText: value }))
                }
                validationMessageConfig={null /* this.validationMessageConfig() */}
                type="text"
                inputWrapperStyle={{}}
                filtering
              />
            </div>

            <div className="breaker mb-5" />

            <div className="modal-buttons">
              <Button id="buttonClearSearch" textButton mr="10px" onClick={clearFilters}>
                {formatMessage("app.clear", "Clear")}
              </Button>

              <Button id="buttonSearch" textButton mr="10px" onClick={filterApplications}>
                {formatMessage("app.search", "Search")}
              </Button>
            </div>
          </div>
        </Drawer>
      </Flex>
    </>
  );
};

export default Applications;
